import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { FilePreviewError } from '../filePreviewError';
import { usePdfDocument } from 'hooks/useFileType/usePdfDocument';
import { logError } from 'setup/logger/logError';
import {
  CONTAINER_SX,
  getCanvasStyle,
  getPageSx,
  LOADING_SX,
  PAGE_OVERLAY_SX,
  PAGES_WRAPPER_SX,
} from './styles';
import { PageStatus, Props } from './types';

export const PdfDisplay: React.FC<Props> = ({ src, onDownload }) => {
  const { loading, error, pdfDocument, numPages } = usePdfDocument(src);
  const [pageStatuses, setPageStatuses] = useState<PageStatus[]>([]);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  useEffect(() => {
    setPageStatuses(
      numPages > 0
        ? Array(numPages)
            .fill(null)
            .map(() => ({ rendered: false, loading: false }))
        : []
    );
    canvasRefs.current = numPages > 0 ? Array(numPages).fill(null) : [];
  }, [src, numPages]);

  useEffect(() => {
    if (!pdfDocument || pageStatuses.length === 0) return;

    let cancelled = false;
    const renderTasks: { cancel: () => void }[] = [];

    const renderPage = async (pageNumber: number): Promise<void> => {
      const canvas = canvasRefs.current[pageNumber - 1];
      if (!canvas || pageStatuses[pageNumber - 1].rendered || cancelled) return;

      setPageStatuses((prev) => {
        const newStatuses = [...prev];
        newStatuses[pageNumber - 1] = { rendered: false, loading: true };
        return newStatuses;
      });

      try {
        const page = await pdfDocument.getPage(pageNumber);
        const context = canvas.getContext('2d');
        if (!context) return;

        const viewport = page.getViewport({ scale: 1 });
        const maxWidth = 800;
        const scale = Math.min(maxWidth / viewport.width, 2);
        const scaledViewport = page.getViewport({ scale });

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const renderTask = page.render({
          canvas: canvas,
          canvasContext: context,
          viewport: scaledViewport,
        });

        renderTasks.push(renderTask);
        await renderTask.promise;

        if (cancelled) {
          return;
        }

        setPageStatuses((prev) => {
          const newStatuses = [...prev];
          newStatuses[pageNumber - 1] = { rendered: true, loading: false };
          return newStatuses;
        });
      } catch (err) {
        const isRenderingCancelledException =
          (err as { name?: string })?.name ===
          'RenderingCancelledException';

        if (cancelled || isRenderingCancelledException) {
          return;
        }

        logError(`Error rendering PDF page ${pageNumber}:`, err);
        setPageStatuses((prev) => {
          const newStatuses = [...prev];
          newStatuses[pageNumber - 1] = {
            rendered: false,
            loading: false,
          };
          return newStatuses;
        });
      }
    };

    const renderAllPages = async (): Promise<void> => {
      for (let i = 1; i <= numPages; i++) {
        await renderPage(i);
      }
    };

    renderAllPages();

    return () => {
      cancelled = true;
      renderTasks.forEach((task) => task.cancel());
    };
  }, [pdfDocument, pageStatuses.length, numPages]);

  if (error) {
    return <FilePreviewError onDownload={onDownload} />;
  }

  if (loading) {
    return (
      <Box sx={LOADING_SX}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={CONTAINER_SX}>
      <Box sx={PAGES_WRAPPER_SX}>
        {Array.from({ length: numPages }, (_, index) => (
          <Box
            key={index}
            sx={getPageSx(pageStatuses[index]?.rendered)}
          >
            {!pageStatuses[index]?.rendered && (
              <Box sx={PAGE_OVERLAY_SX}>
                <CircularProgress size={40} />
              </Box>
            )}
            <canvas
              ref={(el) => {
                canvasRefs.current[index] = el;
              }}
              style={getCanvasStyle(pageStatuses[index]?.rendered)}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
